import React, { useState } from "react";
import Box from "@mui/material/Box";
import List from "@mui/material/List";
import { DRAWER_CONTENT_EXCEPTION } from "../../utils/constants";
import {
  isRestrictedTenant,
  isDashboardUnrestrictedTenant,
  isLimitedMenuTenant,
} from "../../utils/helpers/tenantRestrictions";
import { isDemoTenant } from "../../utils/helpers/tenantConfig";
import { REACHIQ_ICON } from "../../utils/constants/assets";
import {
  DASHBOARD_PATH,
  PEOPLE_SCREEN_PATH,
  SEQUENCE_PATH,
  TEMPLATE_PATH,
  IMPORT_TABLE_CSV,
} from "../../utils/constants/routes";
import SidebarItem from "./SidebarItem";
import { useLocation, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import ImageIcon from "../Icon/ImageIcon";
import ContactFormModal from "../Modals/ContactFormModal";
import QaReporter from "../QaReporter/QaReporter";
import IconComponent from "../Icon/IconComponent";
import { ICON_LABELS, fontWeight } from "../../utils/constants/UI";
import { BASE, BORDER, GREY } from "../../utils/constants/colors";
import TypographyInline from "../Typography/TypographyInline";
import useFirebaseProgressSync from "../../hooks/useFirebaseProgressSync";
import { useSetupWizard } from "../../context/SetupWizardContext";
import GuidedTourBanner from "../GuidedTourBanner/GuidedTourBanner"; // ribbon disabled inside; kept for WelcomeModal
import TrialBanner from "../TrialBanner/TrialBanner";

const styles = {
  sidebarContainer: {
    display: "flex",
  },
  bugReportContainer: {
    paddingTop: "5vh",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    cursor: "pointer",
  },
  drawer: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    padding: "1vh 0 2.5vh 0",
    width: "5rem",
    borderRight: `1px solid ${BORDER.LIGHT}`,
    height: "100vh",
  },

  containIcon: {
    display: "flex",
    justifyContent: "center",
    marginTop: "1rem",
    bgcolor: "background.default",
    cursor: "pointer",
  },
  containBugIcon: {
    background: "rgba(211, 218, 227,0.5)",
    padding: "6px",
    width: "36px",
    height: "36px",
    borderRadius: "50%",
    display: "flex",
    justifyContent: "center",
  },
  childrenContainer: {
    width: "calc(100% - 5rem)",
    background: BASE.BACKGROUND_LIGHT,
    maxHeight: "100vh",
    overflow: "hidden",
  },

  flex: {
    display: "flex",
    borderBottom: "1px solid #D3DAE3",
    padding: "0.6rem 1rem",
    cursor: "pointer",
  },
  navContainer: {
    overflow: "auto",
    background: "white",
  },
  navItemContainer: {
    display: "flex",
    flexDirection: "column",
    gap: "100px",
  },
};

export default function DashboardLayout({ children }) {
  const location = useLocation();
  const navigate = useNavigate();
  const userDetailsSelector = useSelector((state) => state?.userDetails);
  const userDetails = userDetailsSelector?.payload;
  const [openConfirmation, setOpenConfirmation] = useState(false);

  // Firebase progress sync - lives here so listeners persist across all screen navigations
  useFirebaseProgressSync();
  const { wizardActive, wizardPhase } = useSetupWizard();
  const sidebarLocked = wizardActive && wizardPhase === "step";

  const handleModalChange = () => {
    setOpenConfirmation(!openConfirmation);
  };

  const handleSelection = (item) => {
    if (!item?.toolTipElements?.length) {
      item?.path && navigate(item.path);
    }
  };

  const checkSelected = (items) => {
    if (items?.length) {
      const paths = items?.map((item) => item?.toolTipPath);
      return paths?.some(
        (item) =>
          location.pathname === item ||
          location.pathname.startsWith(item + "/"),
      );
    }
    return false;
  };

  // Per-tenant sidebar hiding:
  //   restricted tenant → hide Sequence + Templates (Marketing), always
  //   restricted AND NOT dashboard-unrestricted → also hide CSV Enrichment (Tools)
  //   demo tenant       → hide Sequence + Templates (Marketing)
  //   limited-menu tenant → hide Sequence + Templates (Marketing)
  //   admin-sdr user    → hide Sequence + Templates (Marketing), any tenant;
  //                       the Router guard 403s these paths for SDRs anyway
  // Sequence + Templates are deliberately NOT gated on
  // isDashboardUnrestrictedTenant(). That flag exists to keep the DASHBOARD
  // looking unrestricted; when it also gated this list, workdigital and blazent
  // kept Sequence/Templates visible in Marketing even though every other
  // restricted-tenant rule applied to them (reported on blazent, 2026-08-14).
  const hiddenPaths = [
    ...(isRestrictedTenant() ? [SEQUENCE_PATH, TEMPLATE_PATH] : []),
    ...(isRestrictedTenant() && !isDashboardUnrestrictedTenant()
      ? [IMPORT_TABLE_CSV]
      : []),
    ...(isDemoTenant() ? [SEQUENCE_PATH, TEMPLATE_PATH] : []),
    ...(isLimitedMenuTenant() ? [SEQUENCE_PATH, TEMPLATE_PATH] : []),
    ...(userDetails?.role_access === "admin-sdr"
      ? [SEQUENCE_PATH, TEMPLATE_PATH]
      : []),
  ];
  const drawerItems = hiddenPaths.length
    ? DRAWER_CONTENT_EXCEPTION.map((item) => {
        if (!item?.toolTipElements?.length) return item;
        return {
          ...item,
          toolTipElements: item.toolTipElements.filter(
            (sub) => !hiddenPaths.includes(sub?.toolTipPath),
          ),
        };
      })
    : DRAWER_CONTENT_EXCEPTION;

  return (
    <Box sx={styles.sidebarContainer}>
      {/* GuidedTourBanner: its yellow ribbon is retired (disabled inside the component),
          but it stays mounted — the "Welcome to ReachIQ" modal for new users lives in it. */}
      <GuidedTourBanner />
      <TrialBanner />
      <Box sx={styles.drawer}>
        <Box sx={styles.navItemContainer}>
          <Box sx={styles.containIcon} onClick={() => navigate(DASHBOARD_PATH)}>
            <ImageIcon image={REACHIQ_ICON} width="40px" height="40px" />
          </Box>

          <Box sx={{ ...styles.navContainer, position: "relative" }}>
            {/* Overlay that blocks sidebar clicks while the setup wizard is active */}
            {sidebarLocked && (
              <Box
                title="Navigation is locked while setup is in progress"
                sx={{
                  position: "absolute",
                  inset: 0,
                  zIndex: 10,
                  cursor: "not-allowed",
                  backgroundColor: "rgba(255,255,255,0.55)",
                }}
              />
            )}
            <List>
              {drawerItems.map((item, index) => {
                return (
                  <div key={index} onClick={() => handleSelection(item)}>
                    <SidebarItem
                      itemIndex={index}
                      item={item}
                      selected={
                        (item.path &&
                          (location.pathname === item.path ||
                            location.pathname.startsWith(item.path + "/"))) ||
                        checkSelected(item?.toolTipElements)
                      }
                    />
                  </div>
                );
              })}
            </List>
          </Box>
        </Box>
        {/* Live QA reporter: trigger sits at the bottom of the sidebar rail; the
            dialog/capture/badge logic lives inside the component (renders to a portal). */}
        <QaReporter sidebar />
      </Box>
      <Box component="main" className="riq-app-main" sx={styles.childrenContainer}>
        {children}
      </Box>

      {/* Bug Report Modal */}
      <ContactFormModal open={openConfirmation} setOpen={setOpenConfirmation} />
    </Box>
  );
}
